import { useState, useMemo, useRef, useCallback } from "react";
import { TellescopeFormFieldBaseProps } from "../types";
import { cn, Label } from "@tellescope/ui"; // Assuming this is your utility path
import { useTranslation } from "react-i18next";
import { GripVertical } from "lucide-react";
import { FieldDescription } from "../FieldDescription";

export interface RankingFieldProps extends TellescopeFormFieldBaseProps<"ranking"> {}

const ITEM_HEIGHT = 60;

export const RankingField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  options,
  error,
  touched,
  required,
}: RankingFieldProps) => {
  const { t } = useTranslation("forms");
  const containerRef = useRef<HTMLDivElement>(null);
  const showError = !!(error && touched);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const order = useMemo(() => (value && value.length > 0 ? value : options?.choices || []), [options?.choices, value]);

  const proposedOrder = useMemo(() => {
    if (draggedIndex === null || dragOverIndex === null) return order;

    const newOrder = [...order];
    const item = newOrder[draggedIndex];

    newOrder.splice(draggedIndex, 1);
    if (item) newOrder.splice(dragOverIndex, 0, item);

    return newOrder;
  }, [order, draggedIndex, dragOverIndex]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    setDragOverIndex(index);

    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
    }
  };

  const handleContainerDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      if (!containerRef.current || draggedIndex === null) return;

      const { top } = containerRef.current.getBoundingClientRect();
      const offsetY = e.clientY - top;

      const rawIndex = Math.floor(offsetY / ITEM_HEIGHT);
      const newIndex = Math.max(0, Math.min(rawIndex, order.length - 1));

      setDragOverIndex(newIndex);
    },
    [draggedIndex, order.length]
  );

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();

    if (proposedOrder && draggedIndex !== null && dragOverIndex !== null) {
      const hasChanged = JSON.stringify(proposedOrder) !== JSON.stringify(order);
      if (hasChanged) {
        onChange(proposedOrder);
      }
    }

    handleDragEnd();
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  if (!order || order.length === 0) {
    return (
      <div className={cn("space-y-2", className)}>
        {title && <Label required={required}>{title}</Label>}
        <FieldDescription description={description} htmlDescription={htmlDescription} />
        <p className="text-sm text-muted-foreground">{t("No items to rank")}</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <div
        ref={containerRef}
        className={cn("relative isolate", showError && "border border-destructive rounded-md p-2")}
        onDragOver={handleContainerDragOver}
        onDrop={handleDrop}
        style={{ height: order.length * ITEM_HEIGHT }}
      >
        {order.map((item, originalIndex) => {
          const isDragging = draggedIndex === originalIndex;
          const visualIndex = draggedIndex !== null ? proposedOrder.indexOf(item) : originalIndex;
          const top = visualIndex * ITEM_HEIGHT;

          return (
            <div
              key={item}
              draggable
              onDragStart={(e) => handleDragStart(e, originalIndex)}
              onDragEnd={handleDragEnd}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: `${ITEM_HEIGHT - 8}px`,
                transform: `translateY(${top}px)`,
                transition: isDragging ? "none" : "transform 200ms cubic-bezier(0.2, 0, 0, 1)",
                zIndex: isDragging ? 50 : 10,
              }}
              className={cn(
                "flex items-center gap-2 p-3 border rounded-md bg-background",
                isDragging ? "opacity-50 border cursor-grabbing" : "cursor-grab",
                draggedIndex !== null && !isDragging && "pointer-events-none"
              )}
            >
              <div className="flex justify-center p-1 hover:bg-muted/50 rounded transition-colors">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex items-center gap-3 flex-1 select-none">
                <span className="text-sm">{item}</span>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-sm text-muted-foreground">{t("Drag and drop to re-order the above options")}</p>
      {showError && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
